import React, { useState } from 'react';
import { MapPin, Clock, Send } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthDate: '',
    birthTime: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Frontend-only validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Simulate form submission
    toast({
      title: "Message Received!",
      description: "Thank you for contacting us. We'll respond via WhatsApp shortly.",
    });

    // Open WhatsApp with pre-filled message
    const whatsappMessage = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nBirth Date: ${formData.birthDate}\nBirth Time: ${formData.birthTime}\nMessage: ${formData.message}`;
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      birthDate: '',
      birthTime: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="contact-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Begin Your Cosmic Journey</h2>
          <p className="section-description">
            Reach out for personalized astrological guidance and consultation
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h3 className="contact-info-title">Get in Touch</h3>
            <p className="contact-info-text">
              Connect with us through your preferred channel. We're here to illuminate your path.
            </p>
            
            <div className="contact-details">
              <div className="contact-detail-item">
                <MapPin className="contact-icon" />
                <div>
                  <h4>Location</h4>
                  <p>{contactInfo.address}</p>
                </div>
              </div>
            </div>

            <div className="contact-direct">
              <h4>Quick Contact</h4>
              <a href={`tel:${contactInfo.phone}`} className="contact-link">
                {contactInfo.phone}
              </a>
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=astroclick10@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                {contactInfo.email}
              </a>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="birthDate">Birth Date (Optional)</Label>
                  <Input
                    id="birthDate"
                    name="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="birthTime">Birth Time (Optional)</Label>
                <Input
                  id="birthTime"
                  name="birthTime"
                  type="text"
                  value={formData.birthTime}
                  onChange={handleChange}
                  placeholder="HH:MM AM/PM (e.g., 09:30 AM)"
                />
                <p className="input-hint">Use 12-hour format with AM/PM (e.g., 09:30 AM).</p>
              </div>

              <div className="form-group">
                <Label htmlFor="message">Your Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your questions or what guidance you're seeking..."
                  rows={5}
                  required
                />
              </div>

              <Button type="submit" className="contact-submit">
                <Send size={18} />
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;